import React, { useMemo } from "react";
import moment from "moment";

const COLOR_PALETTES = [
  { // Teal
    groupBg: "#f0fdfa",
    groupText: "#0f766e",
    groupBorder: "#0f766e"
  },
  { // Indigo/Blue
    groupBg: "#e0e7ff",
    groupText: "#3730a3",
    groupBorder: "#4f46e5"
  },
  { // Purple
    groupBg: "#f3e8ff",
    groupText: "#6b21a8",
    groupBorder: "#9333ea"
  },
  { // Amber/Orange
    groupBg: "#fef3c7",
    groupText: "#92400e",
    groupBorder: "#d97706"
  },
  { // Emerald/Green
    groupBg: "#ecfdf5",
    groupText: "#065f46",
    groupBorder: "#059669"
  },
  { // Rose/Pink
    groupBg: "#ffe4e6",
    groupText: "#9f1239",
    groupBorder: "#e11d48"
  }
];

const ListEmployeeProjectHoursReport = React.memo(({ empTask, startDate, endDate, monthlySplitup }) => {
  
  const getMonthsInRange = (start, end) => {
    const monthsList = [];
    if (!start || !end) return monthsList;
    const startMoment = moment(start).clone().startOf('month');
    const endMoment = moment(end).clone().startOf('month');
    while (startMoment.isBefore(endMoment) || startMoment.isSame(endMoment, 'month')) {
      monthsList.push({
        year: startMoment.year(),
        month: startMoment.month() + 1,
        label: startMoment.format("MMM-YY")
      });
      startMoment.add(1, 'month');
    }
    return monthsList;
  };

  const months = useMemo(() => {
    if (!monthlySplitup) return [];
    return getMonthsInRange(startDate, endDate);
  }, [startDate, endDate, monthlySplitup]);

  const colSpan = monthlySplitup ? months.length + 2 : 2;

  const groupedData = useMemo(() => {
    if (!empTask || empTask.length === 0) return [];
    
    const groups = {};
    empTask.forEach(item => {
      const empId = item.employeeId || item.EmployeeId || 0;
      const empName = item.employeeName || item.EmployeeName || "Unknown Employee";
      const projName = item.projectName || item.ProjectName || "Unknown Project";
      const hrs = Number(item.hours || item.Hours || 0);
      const m = Number(item.month || item.Month || 0);
      const y = Number(item.year || item.Year || 0);

      const key = `${empId}_${empName}`;
      if (!groups[key]) {
        groups[key] = {
          employeeId: empId,
          employeeName: empName,
          projectsMap: {}, // projName -> hours or horizontal project details
          totalHours: 0
        };
      }
      
      if (monthlySplitup) {
        if (!groups[key].projectsMap[projName]) {
          groups[key].projectsMap[projName] = {
            projectName: projName,
            monthlyHours: {},
            totalHours: 0
          };
        }
        const monthKey = `${y}_${m}`;
        groups[key].projectsMap[projName].monthlyHours[monthKey] = (groups[key].projectsMap[projName].monthlyHours[monthKey] || 0) + hrs;
        groups[key].projectsMap[projName].totalHours += hrs;
      } else {
        groups[key].projectsMap[projName] = (groups[key].projectsMap[projName] || 0) + hrs;
      }
      
      groups[key].totalHours += hrs;
    });

    return Object.values(groups)
      .filter(group => group.totalHours > 0)
      .map(group => {
      if (monthlySplitup) {
        const projects = Object.values(group.projectsMap).filter(proj => proj.totalHours > 0);
        const monthlySubtotals = {};
        months.forEach(mObj => {
          const monthKey = `${mObj.year}_${mObj.month}`;
          let subtotal = 0;
          projects.forEach(proj => {
            subtotal += proj.monthlyHours[monthKey] || 0;
          });
          monthlySubtotals[monthKey] = subtotal;
        });

        return {
          employeeId: group.employeeId,
          employeeName: group.employeeName,
          projects,
          monthlySubtotals,
          totalHours: group.totalHours
        };
      } else {
        const projects = Object.entries(group.projectsMap)
          .map(([name, hrs]) => ({ projectName: name, hours: hrs }))
          .filter(proj => proj.hours > 0);
        return {
          employeeId: group.employeeId,
          employeeName: group.employeeName,
          projects,
          totalHours: group.totalHours
        };
      }
    });
  }, [empTask, monthlySplitup, months]);

  const monthlyGrandTotals = useMemo(() => {
    if (!monthlySplitup) return {};
    const totals = {};
    months.forEach(mObj => {
      const monthKey = `${mObj.year}_${mObj.month}`;
      let total = 0;
      groupedData.forEach(emp => {
        total += emp.monthlySubtotals[monthKey] || 0;
      });
      totals[monthKey] = total;
    });
    return totals;
  }, [groupedData, monthlySplitup, months]);

  const grandTotal = useMemo(() => {
    return groupedData.reduce((sum, emp) => sum + emp.totalHours, 0);
  }, [groupedData]);

  // Format dates: e.g. "1-Jun-26"
  const formattedFrom = startDate ? moment(startDate).format("D-MMM-YY") : "";
  const formattedTo = endDate ? moment(endDate).format("D-MMM-YY") : "";

  return (
    <div className="premium-report-container" style={{ padding: "0px 10px", backgroundColor: "#fff", width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <style jsx="true">{`
        .premium-report-card {
          width: 100%;
          max-width: ${monthlySplitup ? '100%' : '800px'};
          margin: 0 auto;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #cbd5e1;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.08), 0 2px 4px -1px rgba(0, 0, 0, 0.04);
          overflow: hidden;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
        }
        .premium-report-header {
          background: linear-gradient(135deg, #0f9d8a, #0b7e6e);
          padding: 24px;
          color: #ffffff;
        }
        .premium-report-title {
          font-size: 24px;
          font-weight: 700;
          margin: 0 0 6px 0;
          letter-spacing: -0.5px;
          color: #ffffff;
        }
        .premium-report-subtitle {
          font-size: 14px;
          opacity: 0.9;
          margin: 0;
          font-weight: 400;
          color: #ffffff;
        }
        .premium-meta-table {
          width: 100%;
          border-collapse: collapse;
          background: #f1f5f9;
          border-bottom: 1px solid #cbd5e1;
        }
        .premium-meta-table td {
          padding: 12px 24px;
          font-size: 14px;
          color: #475569;
          font-weight: 500;
        }
        .premium-meta-value {
          font-weight: 700;
          color: #0f172a;
        }
        .premium-table-container {
          width: 100%;
          max-height: calc(100vh - 390px);
          overflow: auto;
          padding: 12px;
        }
        .pdf-printing .premium-report-card {
          border: none !important;
          box-shadow: none !important;
          max-width: 100% !important;
        }
        .pdf-printing .premium-table-container {
          max-height: none !important;
          overflow: visible !important;
          padding: 0 !important;
        }
        .pdf-printing .premium-table-container > div {
          min-width: 100% !important;
        }
        .pdf-printing .premium-table {
          font-size: 11px !important;
          table-layout: auto !important;
          width: 100% !important;
        }
        .pdf-printing .premium-table th,
        .pdf-printing .premium-table td {
          padding: 6px 8px !important;
          min-width: unset !important;
          width: auto !important;
        }
        .pdf-printing .premium-group-header-cell {
          font-size: 13px !important;
          padding: 6px 8px !important;
        }
        .pdf-printing .premium-column-header-cell {
          font-size: 10px !important;
        }
        .pdf-printing .premium-subtotal-label,
        .pdf-printing .premium-subtotal-value {
          font-size: 11px !important;
        }
        .pdf-printing .premium-grand-total-label {
          font-size: 12px !important;
        }
        .pdf-printing .premium-grand-total-value {
          font-size: 12px !important;
        }
        .pdf-printing td:first-child,
        .pdf-printing th:first-child {
          width: 180px !important;
          max-width: 180px !important;
          white-space: normal !important;
        }
        .premium-table-container::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .premium-table-container::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 4px;
        }
        .premium-table-container::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .premium-table-container::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
        .premium-employee-group {
          page-break-inside: avoid;
          break-inside: avoid;
          margin-bottom: 24px;
          border: 1px solid #cbd5e1;
          border-radius: 8px;
          overflow: visible;
          background-color: #ffffff;
        }
        .premium-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 16px;
          color: #1e293b;
        }
        .premium-table th, .premium-table td {
          padding: 14px 24px;
          border-bottom: 1px solid #e2e8f0;
          vertical-align: middle;
        }
        .premium-group-header-cell {
          font-weight: 700;
          font-size: 19px;
          border-bottom: 2px solid #cbd5e1;
          letter-spacing: -0.3px;
        }
        .premium-column-header-row {
          background-color: #f8fafc;
          border-bottom: 1px solid #e2e8f0;
        }
        .premium-column-header-cell {
          font-weight: 700;
          color: #334155;
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          white-space: nowrap;
        }
        .premium-data-row {
          background-color: #ffffff;
        }
        .premium-data-row:hover {
          background-color: #f8fafc;
        }
        .premium-number-cell {
          text-align: right;
          font-variant-numeric: tabular-nums;
          white-space: nowrap;
        }
        .premium-subtotal-row {
          font-weight: 700;
          background-color: #f8fafc;
          border-bottom: none;
          page-break-inside: avoid;
          break-inside: avoid;
        }
        .premium-subtotal-label {
          color: #334155;
          font-size: 15px;
          white-space: nowrap;
        }
        .premium-subtotal-value {
          font-weight: 700;
          font-size: 15px;
        }
        .premium-grand-total-container {
          page-break-inside: avoid;
          break-inside: avoid;
          margin-top: 12px;
          border: 1px solid #0f766e;
          border-radius: 8px;
          overflow: hidden;
        }
        .premium-grand-total-row {
          font-weight: 800;
          background: #0f766e;
          color: #ffffff;
        }
        .premium-grand-total-label {
          color: #ffffff;
          font-size: 17px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 800;
          white-space: nowrap;
        }
        .premium-grand-total-value {
          color: #ffffff;
          font-size: 19px;
          font-weight: 800;
        }
      `}</style>
      
      {groupedData.length > 0 ? (
        <div className="premium-report-card">
          {/* Header Card */}
          <div className="premium-report-header">
            <h4 className="premium-report-title">Employee Project Hours Report</h4>
            <p className="premium-report-subtitle">Consolidated hours logged per employee across projects</p>
          </div>

          {/* Date range details table */}
          <table className="premium-meta-table">
            <tbody>
              <tr>
                <td>From Date: <span className="premium-meta-value">{formattedFrom}</span></td>
                <td style={{ textAlign: "right" }}>To Date: <span className="premium-meta-value">{formattedTo}</span></td>
              </tr>
            </tbody>
          </table>

          {/* Main Data Table */}
          <div className="premium-table-container">
            <div style={{ minWidth: monthlySplitup ? `${390 + 95 * months.length}px` : '100%' }}>
            {groupedData.map((emp, index) => {
              const palette = COLOR_PALETTES[index % COLOR_PALETTES.length];
              return (
                <div key={emp.employeeId || index} className="premium-employee-group">
                  <table className="premium-table">
                    <tbody>
                      {/* Group header row */}
                      <tr className="premium-group-header-row" style={{ backgroundColor: palette.groupBg }}>
                        <td colSpan={colSpan} className="premium-group-header-cell" style={{ color: palette.groupText, borderLeft: `6px solid ${palette.groupBorder}`, whiteSpace: "nowrap" }}>
                          {emp.employeeName}
                        </td>
                      </tr>
                      {/* Column headers row */}
                      <tr className="premium-column-header-row">
                        <td className="premium-column-header-cell" style={{ width: monthlySplitup ? "230px" : "70%", minWidth: monthlySplitup ? "180px" : "auto" }}>Project Name</td>
                        {monthlySplitup ? (
                          <>
                            {months.map((m, mIdx) => (
                              <td key={mIdx} className="premium-column-header-cell premium-number-cell" style={{ minWidth: "80px", width: "80px" }}>{m.label}</td>
                            ))}
                            <td className="premium-column-header-cell premium-number-cell" style={{ minWidth: "90px", width: "90px" }}>Total</td>
                          </>
                        ) : (
                          <td className="premium-column-header-cell premium-number-cell" style={{ width: "30%" }}>Hours Logged</td>
                        )}
                      </tr>
                      {/* Projects list */}
                      {emp.projects.map((proj, pIdx) => (
                        <tr key={pIdx} className="premium-data-row">
                          <td style={{ fontWeight: "600", color: "#1e293b", width: monthlySplitup ? "230px" : "70%", minWidth: monthlySplitup ? "180px" : "auto", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{proj.projectName}</td>
                          {monthlySplitup ? (
                            <>
                              {months.map((m, mIdx) => {
                                const mKey = `${m.year}_${m.month}`;
                                const val = proj.monthlyHours[mKey] || 0;
                                return (
                                  <td key={mIdx} className="premium-number-cell" style={{ minWidth: "80px", width: "80px", fontWeight: val > 0 ? "600" : "400", color: val > 0 ? "#1e293b" : "#94a3b8" }}>
                                    {val.toFixed(2)}
                                  </td>
                                );
                              })}
                              <td className="premium-number-cell" style={{ minWidth: "90px", width: "90px", fontWeight: "700", color: palette.groupText }}>{proj.totalHours.toFixed(2)}</td>
                            </>
                          ) : (
                            <td className="premium-number-cell" style={{ width: "30%", fontWeight: "600", color: palette.groupText }}>{proj.hours.toFixed(2)}</td>
                          )}
                        </tr>
                      ))}
                      {/* Employee Subtotal row */}
                      <tr className="premium-subtotal-row">
                        <td className="premium-subtotal-label" style={{ fontWeight: "700", width: monthlySplitup ? "230px" : "70%", minWidth: monthlySplitup ? "180px" : "auto", whiteSpace: "nowrap" }}>Total for {emp.employeeName}</td>
                        {monthlySplitup ? (
                          <>
                            {months.map((m, mIdx) => {
                              const mKey = `${m.year}_${m.month}`;
                              const val = emp.monthlySubtotals[mKey] || 0;
                              return (
                                <td key={mIdx} className="premium-number-cell premium-subtotal-value" style={{ minWidth: "80px", width: "80px", color: palette.groupText }}>
                                  {val.toFixed(2)}
                                </td>
                              );
                            })}
                            <td className="premium-number-cell premium-subtotal-value" style={{ minWidth: "90px", width: "90px", color: palette.groupText, fontSize: "16px" }}>{emp.totalHours.toFixed(2)}</td>
                          </>
                        ) : (
                          <td className="premium-number-cell premium-subtotal-value" style={{ width: "30%", color: palette.groupText, fontSize: "16px" }}>{emp.totalHours.toFixed(2)}</td>
                        )}
                      </tr>
                    </tbody>
                  </table>
                </div>
              );
            })}

            {/* Grand Total Table */}
            <div className="premium-grand-total-container">
              <table className="premium-table">
                <tbody>
                  <tr className="premium-grand-total-row">
                    <td className="premium-grand-total-label" style={{ fontWeight: "800", width: monthlySplitup ? "230px" : "70%", minWidth: monthlySplitup ? "180px" : "auto", whiteSpace: "nowrap" }}>Grand Total</td>
                    {monthlySplitup ? (
                      <>
                        {months.map((m, mIdx) => {
                          const mKey = `${m.year}_${m.month}`;
                          const val = monthlyGrandTotals[mKey] || 0;
                          return (
                            <td key={mIdx} className="premium-number-cell premium-grand-total-value" style={{ minWidth: "80px", width: "80px" }}>
                              {val.toFixed(2)}
                            </td>
                          );
                        })}
                        <td className="premium-number-cell premium-grand-total-value" style={{ width: "120px" }}>{grandTotal.toFixed(2)}</td>
                      </>
                    ) : (
                      <td className="premium-number-cell premium-grand-total-value" style={{ width: "30%" }}>{grandTotal.toFixed(2)}</td>
                    )}
                  </tr>
                </tbody>
              </table>
            </div>
            </div>
          </div>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#64748b", border: "1px solid #cbd5e1", borderRadius: "12px", backgroundColor: "#f8fafc", margin: "0 auto", maxWidth: "800px" }}>
          <i className="fas fa-folder-open" style={{ fontSize: "32px", color: "#cbd5e1", marginBottom: "12px", display: "block" }}></i>
          No records found for the selected criteria.
        </div>
      )}
    </div>
  );
});

export default ListEmployeeProjectHoursReport;
